'use client'
import React, { useEffect } from "react";

import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "store";
import {
  setUser,
  setUserToken,
  setPrivateCashList,
  setPrivateTourneyList,
} from "store/actions";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { CssBaseline, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { auth } from "config/firebase";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { setAnalyticsUserId } from "utils/analytics";

import Notifications from "sections/Notifications";
import Router from "sections/Router";

import ErrorBoundary from "./Components/ErrorBoundary";
import useUserListeners from "./Hooks/useUserListeners";
import useHostListeners from "./Hooks/useHostListeners";
import useMessageCount from "./Hooks/useMessageCount";

function App() {
  const user = useSelector((state: RootState) => state.user.user);
  const theme = useTheme();
  const claims = useSelector((state: RootState) => state.user.token);
  
  const dispatch = useDispatch<AppDispatch>();

  // Use custom hooks for efficient Firebase listeners
  useUserListeners({ userId: user?.uid || null });
  useHostListeners({ userId: user?.uid || null, isHost: claims?.isHost || false });
  const msgsCount = useMessageCount({ userId: user?.uid || null });

  // Authentication listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        // checks to see if user email is verified
        const userToken = await getIdTokenResult(authUser);
        dispatch(setUser(authUser));
        dispatch(setUserToken(userToken.claims));
        // Set analytics user ID
        setAnalyticsUserId(authUser.uid);
      } else {
        // User is signed out - clear Redux state
        dispatch(setUser(null));
        dispatch(setUserToken(null));
        // Clear private game lists when user logs out
        dispatch(setPrivateCashList({}));
        dispatch(setPrivateTourneyList({}));
        // Clear analytics user ID
        setAnalyticsUserId(null);
      }
    });

    // Cleanup auth listener
    return () => unsubscribe();
  }, [dispatch]);


  return (
    <ErrorBoundary>
      <CssBaseline />
      <Box
        id="app-root"
        sx={{
          fontFamily: theme.typography.fontFamily,
          textAlign: "center",
          fontSize: {
            xs: "70%",
            sm: "100%",
          },
          background: theme.gradients.grey,
        }}
      >
        <Notifications />
        <Router msgsCount={msgsCount} />
        <ToastContainer />
      </Box>
    </ErrorBoundary>
  );
}

export default App;
